import { Box, HStack, VStack, Text, Badge, Mark } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { formatCurrency, formatAmount } from '@/lib/utils/format';
import { CryptoIcon } from '@/components/ui/CryptoIcon';
import type { Order } from '@nktkas/hyperliquid';

interface OpenOrderCardProps {
  order: Order;
}

export function OpenOrderCard({ order }: OpenOrderCardProps) {
  const t = useTranslations('trader.orders');

  // Extract order details
  const symbol = order.coin || 'UNKNOWN';
  const side = order.side === 'B' ? t('buy') : t('sell');
  const orderType = t('limit'); // Default to Limit for now
  const totalAmount = parseFloat(order.origSz ?? '0');
  const filledAmount = totalAmount - (parseFloat(order.sz ?? '0') || 0);

  const orderPrice =
    typeof order.limitPx === 'string'
      ? parseFloat(order.limitPx)
      : order.limitPx || 0;
  const timestamp = order.timestamp
    ? new Date(order.timestamp).toLocaleString()
    : t('recent');

  const totalValue = totalAmount * orderPrice;
  const filledValue = filledAmount * orderPrice;

  return (
    <Box
      bg="white/5"
      border="1px solid"
      borderColor="white/8"
      borderRadius="16px"
      p={4}
      w="full"
    >
      <VStack gap={4} align="stretch">
        {/* Header Section */}
        <HStack justify="space-between" align="center" w="full">
          <HStack gap={2.5} align="center">
            {/* Crypto Icon */}
            <CryptoIcon symbol={symbol} size={32} />

            {/* Symbol and Tags */}
            <VStack gap={0.5} align="start">
              <HStack gap={1} align="center">
                <Text fontSize="14px" fontWeight="600" color="white">
                  {symbol}
                </Text>

                {/* Order Type Badge */}
                <Badge
                  bg="white/10"
                  color="white/80"
                  fontSize="12px"
                  fontWeight="400"
                  px={1}
                  py={0.5}
                  borderRadius="6px"
                  backdropFilter="blur(20px)"
                >
                  {orderType}
                </Badge>

                {/* Side Badge */}
                <Box
                  color={side === 'BUY' ? 'profit' : 'loss'}
                  fontSize="14px"
                  fontWeight="700"
                  px={2}
                  py={0.5}
                  borderRadius="4px"
                  letterSpacing="3%"
                  minW="30px"
                  textAlign="center"
                  fontStyle="italic"
                >
                  {side}
                </Box>
              </HStack>

              {/* Timestamp */}
              <Text fontSize="12px" fontWeight="400" color="white/60">
                {timestamp}
              </Text>
            </VStack>
          </HStack>
        </HStack>

        {/* Details Section */}
        <VStack gap={1} align="stretch">
          {/* Filled / Amount */}
          <HStack justify="space-between" align="center" w="full">
            <Text fontSize="12px" fontWeight="500" color="white/90">
              {<Mark color="white/60">{t('filled')}</Mark>}
              <Mark color="white/90"> / {t('amount')}</Mark>
            </Text>
            <Text
              fontSize="14px"
              fontWeight="500"
              color="white/90"
              textAlign="right"
            >
              <Mark color="white/60">{formatCurrency(filledValue)}</Mark> /{' '}
              <Mark color="white/90">{formatCurrency(totalValue)}</Mark>
            </Text>
          </HStack>

          {/* Order Price */}
          <HStack justify="space-between" align="center" w="full">
            <Text fontSize="12px" fontWeight="500" color="white/60">
              {t('orderPrice')}
            </Text>
            <HStack gap={2} align="center">
              <Text fontSize="14px" fontWeight="500" color="white/90">
                {formatCurrency(orderPrice)}
              </Text>
            </HStack>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
}
